import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ShieldCheck, Truck, Store } from "lucide-react";
import { ASSETS, STORE_INFO } from "@/lib/assets";
import { StoreStatusBadge } from "@/components/StoreStatusBadge";

// Square catalogue categories with the most items on the shelf
const FOOTER_CATEGORIES = [
  { slug: "beauty", name: "Beauty" },
  { slug: "food-uncooked", name: "Food-Uncooked" },
  { slug: "household-item", name: "Household Item" },
  { slug: "hair", name: "Hair" },
];

export const Footer = () => {
  return (
    <footer data-testid="footer" className="mt-24 bg-forest text-sand">
      <div className="kente-band" />
      <div className="pattern-dots">
        {/* Promise strip */}
        <div className="mx-auto grid max-w-[1440px] gap-4 border-b border-white/10 px-5 py-8 sm:grid-cols-3 lg:px-10">
          {[
            { icon: Truck, t: "Same-day delivery", s: "Across Calgary, straight from our shelves" },
            { icon: Store, t: "Free in-store pickup", s: "Order online, collect at Midlake" },
            { icon: ShieldCheck, t: "Secure checkout", s: "Payments protected by Square" },
          ].map((v) => (
            <div key={v.t} className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-ochre">
                <v.icon className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-bold text-white">{v.t}</p>
                <p className="text-xs text-sand/65">{v.s}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-2 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block rounded-3xl bg-white p-4 shadow-lg">
              <img src={ASSETS.logo} alt="Bambam African Mart" className="h-32 w-auto object-contain" />
            </Link>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-sand/75">
              Authentic African groceries, spices and produce — delivered fresh across Calgary, or ready for pickup in store.
            </p>
          </div>

          <div className="lg:col-span-2">
            <h4 className="label-eyebrow text-ochre">Shop</h4>
            <ul className="mt-5 space-y-3 text-sm text-sand/80">
              <li><Link to="/shop" className="transition-colors hover:text-ochre">All Products</Link></li>
              {FOOTER_CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/category/${c.slug}`} className="transition-colors hover:text-ochre">{c.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="label-eyebrow text-ochre">Company</h4>
            <ul className="mt-5 space-y-3 text-sm text-sand/80">
              <li><Link to="/about" className="transition-colors hover:text-ochre">Our Story</Link></li>
              <li><Link to="/account" className="transition-colors hover:text-ochre">My Account</Link></li>
              <li><Link to="/wishlist" className="transition-colors hover:text-ochre">Favourites</Link></li>
              <li><Link to="/cart" className="transition-colors hover:text-ochre">Cart</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2 lg:col-span-4">
            <div className="flex items-center gap-3">
              <h4 className="label-eyebrow text-ochre">Visit Us</h4>
              <StoreStatusBadge className="!bg-white/10 !text-white" />
            </div>
            <ul className="mt-5 space-y-3.5 text-sm text-sand/80">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ochre" />
                <span>
                  {STORE_INFO.address && <>{STORE_INFO.address}<br /></>}
                  {STORE_INFO.city}
                </span>
              </li>
              {STORE_INFO.hoursSummary && (
                <li className="flex gap-3"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-ochre" /><span>{STORE_INFO.hoursSummary}</span></li>
              )}
              <li className="flex gap-3"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-ochre" /><span>{STORE_INFO.phones.join(" · ")}</span></li>
              <li className="flex gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-ochre" />
                <a href={`mailto:${STORE_INFO.email}`} className="transition-colors hover:text-ochre">{STORE_INFO.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-2 px-5 py-6 text-xs text-sand/55 sm:flex-row lg:px-10">
            <span>© {new Date().getFullYear()} Bambam African Mart. All rights reserved.</span>
            <span className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <span>Payments secured by Square · Shipping across Canada</span>
              <span className="hidden sm:inline">·</span>
              <span>
                Crafted by{" "}
                <a
                  data-testid="creator-link"
                  href="https://www.aadeelings.ca/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-ochre transition-colors hover:text-white"
                >
                  AADeelings Inc.
                </a>
              </span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
